
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { TooltipComponent } from '../../../../../shared/components/ui/tooltip/tooltip.component';
import { CardsComponent } from '../../shared/cards/cards.component';
import { TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { ApiService } from '../../../../../core/services/api.service';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';


type PixelValue = `${number}px`;
type colorCode = `#${string}`;

interface toggleHeader {
  heading: string;
  showTable: boolean;
  showlabels: boolean;
  showToggleButton: boolean;
  headingColor: colorCode;
  headingCursor: string;
  headerChangeView: boolean;
  showHeading?: boolean;
  iconPath?: string;
  showIcon?: boolean;
  fontSize?: PixelValue;
  toggleIconPath?: string;
}

interface ToggleOption {
  label: string;
  icon?: string;
  tooltip?: string;
  isTable?: boolean;
}

@Component({
  selector: 'app-business-industries-stats',
  templateUrl: './business-industries-stats.component.html',
  styleUrl: './business-industries-stats.component.scss',
  providers: [],
  imports: [
    CommonModule,
    CardsComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})

export class BusinessIndustriesStatsComponent implements OnInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() showComponent: WritableSignal<boolean> = signal(false);
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;

  headerOptions: toggleHeader = {
    heading: '',
    headingColor: '#0C66E8',
    headerChangeView: true,
    headingCursor: 'pointer',
    showTable: false,
    showlabels: true,
    showToggleButton: false,
  };

  @Input() toggleOptions: ToggleOption[] = [
    {
      label: 'Graph',
      icon: 'assets/img/PNG/Vector.png',
      tooltip: 'Click to View Graph',
      isTable: false,
    },
    {
      label: 'Table',
      icon: 'assets/img/PNG/show_table.png',
      tooltip: 'Click to View Table',
      isTable: true,
    },
  ];

  contracts: any = {
    chart_value_type: 1,
    contractsData: [],
    chart_data_previous: [],
    dentalData: {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
    },
    healthcareData: {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
    },
    nonHealthcareData: {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
    },
  };

  tooltip = [
    { label: 'Dental', key: 'dental' },
    { label: 'Healthcare', key: 'healthcare' },
    { label: 'Non-Healthcare', key: 'non_healthcare' },
  ];
  showTable: boolean = true;
  showIndustryTable: boolean = true;
  headingTable: string = 'View industry-wise segregation';

  industryCards = [
    {
      label: 'Dental',
      dataKey: 'dentalData',
      index: 1,
      primaryLineColor: '#FFBF47',
      valueKey: 'value',
      tableHeaders: [
        { label: 'Period', key: 'start', align: 'text-left' as const },
        { label: 'Dental', key: 'dental', align: 'text-center' as const },
      ],
    },
    {
      label: 'Non-Healthcare',
      dataKey: 'nonHealthcareData',
      index: 2,
      primaryLineColor: '#3DE195',
      valueKey: 'value',
      tableHeaders: [
        { label: 'Period', key: 'start', align: 'text-left' as const },
        { label: 'Non-Healthcare', key: 'non_healthcare', align: 'text-center' as const },
      ],
    },
    {
      label: 'Healthcare',
      dataKey: 'healthcareData',
      index: 3,
      primaryLineColor: '#0C66E8',
      valueKey: 'value',
      tableHeaders: [
        { label: 'Period', key: 'start', align: 'text-left' as const },
        { label: 'Healthcare', key: 'healthcare', align: 'text-center' as const },
      ],
    },
  ];

  private viewSignal: WritableSignal<'chart' | 'table'> = signal('chart');


  constructor(private apiService: ApiService) { }

  ngOnInit() {
    // Initialize empty data structures
    this.initializeIndustryData();
    
    this.headerOptions = {
      heading: this.headingTable,
      headingColor: '#0C66E8',
      headerChangeView: true,
      headingCursor: 'pointer',
      showTable: this.showIndustryTable,
      showlabels: true,
      showToggleButton: false,
    };

    // Fetch data on initial load if dateRange is available
    if (this.dateRange) {
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        // Fetch both current and previous data
        this.FetchIndustryBusiness(false, true, false, false); // Fetch current data without processing
        this.FetchIndustryBusiness(false, false, true, true); // Fetch previous data and process all data
      } else {
        // Fetch only current data and process
        this.FetchIndustryBusiness();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Skip first change since we handle initial load in ngOnInit
    if ((changes['dateRange'] && !changes['dateRange'].firstChange && changes['dateRange'].currentValue) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange) ||
        (changes['productServerName'] && !changes['productServerName'].firstChange)) {
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        // Fetch both current and previous data
        this.FetchIndustryBusiness(false, true, false, false); // Fetch current data without processing
        this.FetchIndustryBusiness(false, false, true, true); // Fetch previous data and process all data
      } else {
        // Fetch only current data and process
        this.FetchIndustryBusiness();
      }
    }
  }

  FetchIndustryBusiness(
    useCache?: boolean,
    useLoader: boolean = false,
    isPrevious: boolean = false,
    createChart: boolean = true
  ) {
    try {
      this.contracts.loading = useLoader;

      const payload: any = {
        start_time: (isPrevious
          ? this.dateRange.previous_start_date
          : this.dateRange.start_date) || new Date(),
        end_time: (isPrevious
          ? this.dateRange.previous_end_date
          : this.dateRange.end_date) || new Date(),
        is_dummy: 0,
        // timezone: 'PDT',
        unit: this.dateRange?.unit || 'day',
        outputFormat: 'db',
        stage: 1,
        cocu_id: this.cocuId,
        country_id: this.countryId
      };

      if (isPrevious) {
        this.contracts.dentalData.previousrecords = [];
        this.contracts.healthcareData.previousrecords = [];
        this.contracts.nonHealthcareData.previousrecords = [];
      } else {
        this.contracts.dentalData.records = [];
        this.contracts.healthcareData.records = [];
        this.contracts.nonHealthcareData.records = [];
      }

      if (this.productServerName === 'credee') {
        payload['timezone'] = 'PDT';
      }

      this.apiService
        .post(`/visionboard/stats/industry_business`, payload, {
          addAccessToken: this.requiresAccessToken,
          productServerName: this.productServerName,
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: false,
        })
        .subscribe({
          next: (response: any) => {
            if (response?.is_error === 0 && response?.data) {
             

              // API returns nested objects: dentalData, healthcareData, nonHealthcareData
              // Each contains their own records and stats
              
              if (isPrevious) {
                // Update previous period data
                this.contracts.dentalData.previousrecords =
                  response.data.dentalData?.records || [];
                this.contracts.dentalData.previousstats =
                  response.data.dentalData?.stats || {};
 
                this.contracts.healthcareData.previousrecords =
                  response.data.healthcareData?.records || [];
                this.contracts.healthcareData.previousstats =
                  response.data.healthcareData?.stats || {};

                this.contracts.nonHealthcareData.previousrecords =
                  response.data.nonHealthcareData?.records || [];
                this.contracts.nonHealthcareData.previousstats =
                  response.data.nonHealthcareData?.stats || {};
              } else {
                // Update current period data
                this.contracts.dentalData.records =
                  response.data.dentalData?.records || [];
                this.contracts.dentalData.stats =
                  response.data.dentalData?.stats || {};

                this.contracts.healthcareData.records =
                  response.data.healthcareData?.records || [];
                this.contracts.healthcareData.stats =
                  response.data.healthcareData?.stats || {};

                this.contracts.nonHealthcareData.records =
                  response.data.nonHealthcareData?.records || [];
                this.contracts.nonHealthcareData.stats =
                  response.data.nonHealthcareData?.stats || {};
              }

              // Clear previous data if comparison period is not selected
              if (
                !isPrevious &&
                !(
                  this.dateRange.previous_start_date &&
                  this.dateRange.previous_end_date
                )
              ) {
                this.contracts.dentalData.previousrecords = [];
                this.contracts.dentalData.previousstats = {};
                this.contracts.healthcareData.previousrecords = [];
                this.contracts.healthcareData.previousstats = {};
                this.contracts.nonHealthcareData.previousrecords = [];
                this.contracts.nonHealthcareData.previousstats = {};
              }

              // Create chart data after fetching
              if (createChart) {
                this.processIndustryData();
              }
            }
            this.contracts.loading = false;
          },
          error: (error: any) => {
            this.contracts.loading = false;
            console.error('Error fetching industry stats:', error);
          }
        });
    } catch (error) {
      this.contracts.loading = false;
      console.error('Error fetching industry stats:', error);
    }
  }

  createMainChart() {
    try {
      const formatCurrency = this.contracts.chart_value_type !== 1;

      const createBarData = (
        records: any[],
        previous: any[],
        label: string
      ) => [
        records.map((d: any) => ({
          ...d,
          value: d['total_contracts'],
          tooltip: `
            <div class="text-base">
              <div class="text-[#656B73]">${label}</div>
              <div class="text-[#131921]">${d.total_doctors} Business</div>
            </div>${previous.length ? ', ' : ''}`,
        })),
        previous.map((d: any) => ({
          ...d,
          value: d['total_contracts'],
          tooltip: `
            <div class="text-base">
              <div class="text-[#656B73]">${label}</div>
              <div class="text-[#131921]">${d.total_doctors} Business</div>
            </div>`,
        })),
      ];

      this.contracts.dentalData.barData = createBarData(
        this.contracts.dentalData.records,
        this.contracts.dentalData.previousrecords,
        'Dental'
      );
      this.contracts.healthcareData.barData = createBarData(
        this.contracts.healthcareData.records,
        this.contracts.healthcareData.previousrecords,
        'Health Care'
      );
      this.contracts.nonHealthcareData.barData = createBarData(
        this.contracts.nonHealthcareData.records,
        this.contracts.nonHealthcareData.previousrecords,
        'Non-HealthCare'
      );

      this.contracts.loading = false;
    } catch (error) {
      console.error(error);
    }
  }

  private initializeIndustryData(): void {
    // Initialize empty data structures for each industry
    this.contracts.dentalData = {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
      chartData: [],
      previousChartData: [],
      tableData: [],
      previousTableData: [],
      summary: {
        current: 0,
        previous: 0,
      }
    };

    this.contracts.healthcareData = {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
      chartData: [],
      previousChartData: [],
      tableData: [],
      previousTableData: [],
      summary: {
        current: 0,
        previous: 0,
      }
    };

    this.contracts.nonHealthcareData = {
      stats: {},
      previousstats: {},
      records: [],
      previousrecords: [],
      barData: [],
      chartData: [],
      previousChartData: [],
      tableData: [],
      previousTableData: [],
      summary: {
        current: 0,
        previous: 0,
      }
    };

    this.contracts.loading = false;
  }

  private processIndustryData(): void {
    // Process dental data
    const dentalRecords = this.contracts.dentalData.records || [];
    const dentalPreviousRecords = this.contracts.dentalData.previousrecords || [];
    
    // Each industry has its own records with a 'total' or 'total_doctors' field
    this.contracts.dentalData.chartData = dentalRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
      key: 'Current'
    }));

    this.contracts.dentalData.previousChartData = dentalPreviousRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
      key:'Previous'
    }));

    // Map table data with proper column keys
    this.contracts.dentalData.tableData = dentalRecords.map((item: any) => ({
      ...item,
      dental: item.total || item.total_doctors || 0,
    }));
    this.contracts.dentalData.previousTableData = dentalPreviousRecords.map((item: any) => ({
      ...item,
      dental: item.total || item.total_doctors || 0,
    }));

    // Calculate dental summary
    const dentalStats = this.contracts.dentalData.stats || {};
    const dentalPreviousStats = this.contracts.dentalData.previousstats || {};
    this.contracts.dentalData.summary = {
      current: dentalStats.total || dentalStats.total_doctors || 0,
      previous: dentalPreviousStats.total || dentalPreviousStats.total_doctors || 0,
    };

    // Process healthcare data
    const healthcareRecords = this.contracts.healthcareData.records || [];
    const healthcarePreviousRecords = this.contracts.healthcareData.previousrecords || [];
    
    this.contracts.healthcareData.chartData = healthcareRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
       key: 'Current'
    }));

    this.contracts.healthcareData.previousChartData = healthcarePreviousRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
       key: 'Previous'
    }));

    // Map table data with proper column keys
    this.contracts.healthcareData.tableData = healthcareRecords.map((item: any) => ({
      ...item,
      healthcare: item.total || item.total_doctors || 0,
    }));
    this.contracts.healthcareData.previousTableData = healthcarePreviousRecords.map((item: any) => ({
      ...item,
      healthcare: item.total || item.total_doctors || 0,
    }));

    // Calculate healthcare summary
    const healthcareStats = this.contracts.healthcareData.stats || {};
    const healthcarePreviousStats = this.contracts.healthcareData.previousstats || {};
    this.contracts.healthcareData.summary = {
      current: healthcareStats.total || healthcareStats.total_doctors || 0,
      previous: healthcarePreviousStats.total || healthcarePreviousStats.total_doctors || 0,
    };

    // Process non-healthcare data
    const nonHealthcareRecords = this.contracts.nonHealthcareData.records || [];
    const nonHealthcarePreviousRecords = this.contracts.nonHealthcareData.previousrecords || [];
    
    this.contracts.nonHealthcareData.chartData = nonHealthcareRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
       key: 'Current'
    }));

    this.contracts.nonHealthcareData.previousChartData = nonHealthcarePreviousRecords.map((item: any) => ({
      start: item.start,
      end: item.end,
      value: item.total || item.total_doctors || 0,
       key: 'Previous'
    }));

    // Map table data with proper column keys
    this.contracts.nonHealthcareData.tableData = nonHealthcareRecords.map((item: any) => ({
      ...item,
      non_healthcare: item.total || item.total_doctors || 0,
    }));
    this.contracts.nonHealthcareData.previousTableData = nonHealthcarePreviousRecords.map((item: any) => ({
      ...item,
      non_healthcare: item.total || item.total_doctors || 0,
    }));

    // Calculate non-healthcare summary
    const nonHealthcareStats = this.contracts.nonHealthcareData.stats || {};
    const nonHealthcarePreviousStats = this.contracts.nonHealthcareData.previousstats || {};
    this.contracts.nonHealthcareData.summary = {
      current: nonHealthcareStats.total || nonHealthcareStats.total_doctors || 0,
      previous: nonHealthcarePreviousStats.total || nonHealthcarePreviousStats.total_doctors || 0,
    };

    this.contracts.loading = false;
  }

  onViewChanged(view: any) {
    this.showTable = view !== 'graph';
  }

  showHideIndustry(event: any) {
    this.showIndustryTable = !this.showIndustryTable;
    this.headingTable = `${
      this.showIndustryTable ? 'Hide' : 'View'
    } industry-wise segregation`;
  }

    onToggle(event: any) {
    // ToggleBtnsComponent emits ViewMode: 'graph' | 'table'
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    // Fallback for boolean or object payloads
    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }


}
